/**
 * forwardWheelBehindOverlay — let wheel gestures scroll content visually
 * BEHIND a full-viewport overlay (FS-569).
 *
 * The board's document-preview backdrop is a fixed, viewport-covering layer:
 * it must stay pointer-interactive (click-outside closes the panel, the dim
 * communicates modality), but that also swallows wheel events — so the chat
 * panel behind it froze. `pointer-events: none` is not an option (clicks would
 * leak through to the canvas), and re-dispatching a synthetic WheelEvent does
 * nothing because untrusted events don't trigger native scrolling. Instead,
 * find what's under the cursor beneath the overlay and scroll the nearest
 * scrollable ancestor manually.
 *
 * Deliberate behaviors:
 *  - Only fires for wheels on the dim itself — events bubbling from the panel
 *    keep their native handling (the caller guards on `target === currentTarget`).
 *  - The tldraw canvas is not an overflow scroller, so wheeling over it is a
 *    no-op: the board behind a modal must not zoom/pan.
 */
/** Pixels per line for line-mode wheel deltas (Firefox + physical wheel). */
const LINE_HEIGHT_PX = 16

export function forwardWheelBehindOverlay(
  overlayRoot: HTMLElement,
  clientX: number,
  clientY: number,
  deltaY: number,
  deltaMode: number = 0,
): boolean {
  // Normalize the delta to pixels. deltaMode 1 = lines (Firefox with a
  // physical mouse wheel reports ~3 lines per notch — 3px would be an
  // invisible scroll), deltaMode 2 = pages.
  const deltaPx =
    deltaMode === 1
      ? deltaY * LINE_HEIGHT_PX
      : deltaMode === 2
        ? deltaY * window.innerHeight
        : deltaY

  const stack = document.elementsFromPoint(clientX, clientY)
  const under = stack.find(
    (el): el is HTMLElement => el instanceof HTMLElement && !overlayRoot.contains(el),
  )

  let node: HTMLElement | null = under ?? null
  while (node) {
    const { overflowY } = getComputedStyle(node)
    const scrollable =
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      node.scrollHeight > node.clientHeight
    if (scrollable) {
      node.scrollBy({ top: deltaPx })
      return true
    }
    node = node.parentElement
  }
  return false
}
